import { askConcierge, type ConciergeTurn } from '../services/conciergeService';
import { speak } from '../services/voiceService';
import { t, voiceLang } from '../i18n';
import { escapeHtml } from '../utils/html';
import { logEvent } from '../services/debugLog';
import type { Vec3 } from '../types';

/**
 * In-AR concierge chat: type or speak a question ("nearest restroom?",
 * "where can I get coffee?"), get a grounded answer; when the answer points
 * somewhere, a "Take me there" button starts navigation.
 * Voice input via webkitSpeechRecognition (Chrome/Safari); mic hidden where
 * unsupported. Replies are also spoken when voice guidance is on.
 */

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

function getRecognizer(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export class ConciergeSheet {
  readonly el: HTMLDivElement;
  private messages: HTMLDivElement;
  private input: HTMLInputElement;
  private micBtn: HTMLButtonElement | null = null;
  private recognizer: SpeechRecognitionLike | null = null;
  private recognizing = false;
  /** Conversation memory sent to the agent (last 8 turns). */
  private history: ConciergeTurn[] = [];

  constructor(
    private getPosition: () => Vec3 | null,
    private onNavigate: (poiId: string) => void,
    private onStartTour: (tourId: string) => void,
    private onStartRoute: (poiIds: string[]) => void,
    /** v8: agent extracted a new-POI bookmark — caller arms tap-to-place. */
    private onSuggestPoi: (s: { name: string; category?: string; description?: string }) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'sheet concierge-sheet hidden';
    this.el.innerHTML = `<div class="sheet-handle"></div><h2>💬 ${escapeHtml(t('concierge.title'))}</h2>`;
    this.el.querySelector('.sheet-handle')!.addEventListener('click', () => this.hideAndStopMic());

    this.messages = document.createElement('div');
    this.messages.className = 'concierge-messages';

    const row = document.createElement('div');
    row.className = 'concierge-input-row';
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.className = 'search-input';
    this.input.placeholder = t('concierge.placeholder');
    this.input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') void this.send();
    });
    row.appendChild(this.input);

    // No separate mic button (user feedback: double permission friction).
    // Opening the sheet via 💬 starts listening immediately — the mic
    // permission prompt appears right then, and speech lands in the input.
    this.recognizer = getRecognizer();

    const sendBtn = document.createElement('button');
    sendBtn.className = 'icon-btn';
    sendBtn.textContent = '➤';
    sendBtn.addEventListener('click', () => void this.send());
    row.appendChild(sendBtn);

    this.el.append(this.messages, row);
  }

  show(): void {
    this.el.classList.remove('hidden');
    this.input.placeholder = t('concierge.placeholder'); // language may have changed
    this.input.focus();
    // Voice-first: start listening as the sheet opens (single permission
    // prompt). Typing still works — recognition stops on first result/error.
    if (this.recognizer && !this.recognizing) this.toggleMic(this.recognizer);
  }

  hideAndStopMic(): void {
    if (this.recognizer && this.recognizing) this.recognizer.stop();
    this.hide();
  }

  hide(): void {
    this.el.classList.add('hidden');
  }

  get visible(): boolean {
    return !this.el.classList.contains('hidden');
  }

  private toggleMic(rec: SpeechRecognitionLike): void {
    if (this.recognizing) {
      rec.stop();
      return;
    }
    rec.lang = voiceLang();
    rec.interimResults = false;
    rec.onresult = (ev) => {
      const transcript = ev.results[0]?.[0]?.transcript ?? '';
      if (transcript) {
        this.input.value = transcript;
        void this.send();
      }
    };
    rec.onend = () => {
      this.recognizing = false;
      this.micBtn?.classList.remove('on');
    };
    rec.onerror = () => {
      this.recognizing = false;
      this.micBtn?.classList.remove('on');
      logEvent('error', 'speech recognition failed');
    };
    this.recognizing = true;
    this.micBtn?.classList.add('on');
    rec.start();
  }

  private addBubble(text: string, who: 'user' | 'bot'): HTMLDivElement {
    const b = document.createElement('div');
    b.className = `concierge-bubble ${who}`;
    b.textContent = text;
    this.messages.appendChild(b);
    this.messages.scrollTop = this.messages.scrollHeight;
    return b;
  }

  private async send(): Promise<void> {
    const q = this.input.value.trim();
    if (!q) return;
    this.input.value = '';
    this.addBubble(q, 'user');
    const pending = this.addBubble(t('concierge.thinking'), 'bot');

    try {
      const res = await askConcierge(q, this.getPosition(), this.history);
      pending.textContent = res.reply;
      speak(res.reply, 'concierge', 0);
      // Remember the exchange (agent memory for follow-ups).
      this.history.push({ role: 'user', content: q }, { role: 'assistant', content: res.reply });
      if (this.history.length > 8) this.history = this.history.slice(-8);

      const targetPoi = res.poiId;
      const targetTour = res.tourId;
      const targetRoute = res.poiIds;
      const suggestion = res.suggestPoi;
      if (targetPoi || targetTour || targetRoute) {
        const go = document.createElement('button');
        go.className = 'btn primary concierge-go';
        go.textContent = targetRoute ? t('concierge.goRoute') : t('concierge.go');
        go.addEventListener('click', () => {
          this.hideAndStopMic();
          if (targetRoute) this.onStartRoute(targetRoute);
          else if (targetPoi) this.onNavigate(targetPoi);
          else if (targetTour) this.onStartTour(targetTour);
        });
        this.messages.appendChild(go);
        this.messages.scrollTop = this.messages.scrollHeight;
      } else if (suggestion) {
        const place = document.createElement('button');
        place.className = 'btn primary concierge-go';
        place.textContent = t('concierge.placeIt');
        place.addEventListener('click', () => {
          this.hideAndStopMic();
          this.onSuggestPoi(suggestion);
        });
        this.messages.appendChild(place);
        this.messages.scrollTop = this.messages.scrollHeight;
      }
    } catch (err) {
      pending.textContent = err instanceof Error ? err.message : String(err);
    } finally {
      // Voice-first design (no persistent mic button — see constructor
      // comment): Web Speech recognition stops itself after one result
      // (interimResults:false), so without re-arming it here a spoken
      // conversation could only ever have ONE exchange — the user would
      // have to close and reopen the whole sheet to ask a follow-up by
      // voice (reported: "it's only taking one conversation"). Re-listen
      // after every reply, same as the initial show(), as long as the
      // sheet is still open (a "Take me there" tap closes it first).
      if (this.recognizer && this.visible && !this.recognizing) {
        this.toggleMic(this.recognizer);
      }
    }
  }
}
